import {
    Box,
    Flex,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    IconButton,
    HStack,
    VStack,
    Spinner,
    Checkbox,
    Center,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Button,
    useDisclosure,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Tooltip,
    Tag
} from '@chakra-ui/react';
import {
    FiEdit,
    FiTrash2,
    FiMoreVertical,
    FiSmartphone,
} from 'react-icons/fi';
import Pagination from 'react-js-pagination';
import { IoChatbubbleOutline } from "react-icons/io5";
import { LuTag, LuUsers } from 'react-icons/lu';
import { FaAndroid, FaApple } from 'react-icons/fa';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Generates a consistent color for a given string
 * Returns background and text color for outlined badge style
 */
const getConsistentColor = (str) => {
    // Array of vibrant color combinations (background, border, text)
    const colors = [
        { bg: '#F0E7FF', border: '#9B51E0', text: '#6B21A8' },      // Purple
        { bg: '#FFE7F0', border: '#E91E8C', text: '#BE185D' },      // Pink
        { bg: '#E7F0FF', border: '#2D9CDB', text: 'var(--brand-primary-active)' },      // Blue
        { bg: '#E7FFFF', border: '#27AE60', text: '#047857' },      // Cyan
        { bg: '#E7FFF4', border: '#219653', text: '#065F46' },      // Teal/Green
        { bg: '#FFF4E7', border: '#F2994A', text: '#C2410C' },      // Orange
        { bg: '#FFE7E7', border: '#EB5757', text: '#B91C1C' },      // Red
        { bg: '#FFFAE7', border: '#F2C94C', text: '#A16207' },      // Yellow
        { bg: '#F3E7FF', border: '#BB6BD9', text: '#7E22CE' },      // Light Purple
        { bg: '#E7FFF9', border: '#56CCF2', text: '#0E7490' },      // Light Cyan
        { bg: '#FFE7EB', border: '#F2709C', text: '#BE123C' },      // Light Pink
        { bg: '#E7F4FF', border: '#2F80ED', text: 'var(--brand-primary-hover)' },      // Sky Blue
        { bg: '#F9E7FF', border: '#C471ED', text: '#86198F' },      // Violet
        { bg: '#FFF0E7', border: '#FF6B35', text: '#C2410C' },      // Coral
        { bg: '#E7FFE9', border: '#6FCF97', text: '#15803D' },      // Light Green
    ];

    // Simple hash function to convert string to number
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

/**
 * Truncates subscriber ID and provides full ID on hover
 */
const truncateSubscriberId = (id) => {
    if (!id) return '';
    if (id.length <= 12) return id;
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
};

/**
 * Gets platform icon based on device or status
 */
const getPlatformIcon = (subscriber) => {
    // You can customize this logic based on your data structure
    // For now, checking device_id pattern or connection_id
    if (subscriber?.device_id) {
        // Simple heuristic: longer UUIDs might be iOS, shorter might be Android
        // Adjust this logic based on your actual platform identification
        return subscriber.device_id.includes('-') ? 'ios' : 'android';
    }
    return null;
};

const DeleteConfirmationDialog = ({ isOpen, onClose, contact, onDelete, isDeleting }) => {
    const cancelRef = useRef();

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Delete Contact
                    </AlertDialogHeader>

                    <AlertDialogBody>
                        Are you sure you want to delete{' '}
                        <Text as="span" fontWeight="bold">{contact?.name}</Text>?
                        This action cannot be undone.
                    </AlertDialogBody>

                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={onClose} disabled={isDeleting}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={onDelete}
                            ml={3}
                            isLoading={isDeleting}
                            loadingText="Deleting..."
                        >
                            Delete
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

export const ContactsTable = ({
    contacts,
    onEdit,
    onDelete,
    isLoading,
    pagination,
    onPageChange,
    selectedContacts,
    onSelectContact,
    onSelectAll,
    onBulkDelete,
    onBulkEdit,
    listPermission,
    editPermission,
    deletePermission,
    permissionsLoading,
    onNavigate,
    countries = []
}) => {
    const countryMap = countries.reduce((acc, c) => {
        acc[c.id] = c.name;
        return acc;
    }, {});
    const isAllSelected = contacts.length > 0 && selectedContacts.length === contacts.length;
    const isIndeterminate = selectedContacts.length > 0 && selectedContacts.length < contacts.length;

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedContactForDelete, setSelectedContactForDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const navigate = useNavigate()

    // Render order: permissions loading -> No Access -> contacts loading -> table.
    // (Previously checked isLoading first, so a user without contact-list spun
    // forever since the fetch is skipped and isLoading never flipped to false.)
    if (permissionsLoading) {
        return (
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
                <Flex justify="center" align="center" h="200px">
                    <Spinner size="xl" color="blue.500" />
                </Flex>
            </Box>
        );
    }

    if (!listPermission) {
        return (
            <Box className="box-style-card">
                <Center h="400px">
                    <VStack spacing={3}>
                        <Box color="gray.400" fontSize="4xl">
                            <LuUsers />
                        </Box>
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700">
                            No Access
                        </Text>
                        <Text fontSize="sm" color="gray.400" textAlign="center">
                            You don't have permission to view this section.
                        </Text>
                    </VStack>
                </Center>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
                <Flex justify="center" align="center" h="200px">
                    <Spinner size="xl" color="blue.500" />
                </Flex>
            </Box>
        );
    }

    const handleNavigate = (id) => {
        navigate(`/contacts-report/${id}`)
    }

    const handleDeleteClick = (contact) => {
        setSelectedContactForDelete(contact);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        if (!selectedContactForDelete) return;

        setIsDeleting(true);
        try {
            await onDelete(selectedContactForDelete._id || selectedContactForDelete.id);
            onClose();
            setSelectedContactForDelete(null);
        } catch (error) {
            console.error('Delete failed:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
                <Box p={6} borderBottom="1px" borderColor="gray.200">
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                                All Contacts
                            </Text>
                            <Text fontSize="sm" color="gray.400" mt={1}>
                                Manage your customer contacts and organize them with tags
                            </Text>
                        </Box>
                        {pagination.total > 0 && (
                            <Text fontSize="sm" color="gray.600">
                                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
                                {Math.min(pagination.currentPage * pagination.limit, pagination.total)} of{' '}
                                {pagination.total} contacts
                            </Text>
                        )}
                    </Flex>
                </Box>

                <TableContainer>
                    <Table variant="simple" sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <Thead bg="gray.50">
                            <Tr>
                                <Th w="4%" py={3} borderColor="gray.200">
                                    <Checkbox
                                        isChecked={isAllSelected}
                                        isIndeterminate={isIndeterminate}
                                        onChange={onSelectAll}
                                        colorScheme="blue"
                                    />
                                </Th>
                                <Th w="14%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase">
                                    Customer Name
                                </Th>
                                <Th w="14%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase">
                                    Phone / Email
                                </Th>
                                <Th w="10%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase">
                                    Whatsapp
                                </Th>
                                <Th w="12%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase">
                                    Subscriber
                                </Th>
                                <Th w="20%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase" >
                                    Tags & Cohorts
                                </Th>
                                <Th w="10%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase">
                                    Country/Timezone
                                </Th>
                                <Th w="10%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase">
                                    Last Updated
                                </Th>
                                <Th w="6%" py={3} borderColor="gray.200" color="gray.600" fontWeight="medium" fontSize="xs" textTransform="uppercase">
                                    Actions
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {contacts.length === 0 ? (
                                <Tr>
                                    <Td colSpan={9} textAlign="center" py={12} borderColor="gray.200">
                                        <VStack spacing={3}>
                                            <Box color="gray.400" fontSize="3xl">
                                                <LuUsers />
                                            </Box>
                                            <Text fontWeight="medium" color="gray.700">
                                                No contacts found
                                            </Text>
                                            <Text fontSize="sm" color="gray.400">
                                                Get started by adding your first contact
                                            </Text>
                                        </VStack>
                                    </Td>
                                </Tr>
                            ) : (
                                contacts.map((contact) => (
                                    <Tr
                                        key={contact._id || contact.id}
                                        _hover={{ bg: 'gray.50' }}
                                        transition="background 0.2s"
                                    >
                                        <Td py={4} borderColor="gray.200">
                                            <Checkbox
                                                isChecked={selectedContacts.includes(contact._id || contact.id)}
                                                onChange={() => onSelectContact(contact._id || contact.id)}
                                                colorScheme="blue"
                                            />
                                        </Td>
                                        <Td py={4} borderColor="gray.200">
                                            {contact.name ? (
                                                <Flex alignItems="center" gap={3}>
                                                    <Text fontWeight="medium" color="gray.900" whiteSpace="normal">
                                                        {contact.name}
                                                    </Text>
                                                </Flex>
                                            ) : (
                                                <Flex alignItems="center" gap={2}>
                                                    <Text fontSize="sm" color="gray.400" fontStyle="italic" whiteSpace="normal">
                                                        No name
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Td>
                                        <Td py={4} borderColor="gray.200">
                                            {/* A contact is created with a phone, an email, or both, so each
                                                line is rendered only when present. Both open the contact
                                                report — otherwise an email-only contact would have nothing
                                                clickable in this column. */}
                                            <VStack align="start" spacing={1}>
                                                {contact.phone ? (
                                                    <Text
                                                        color="var(--brand-primary)"
                                                        cursor="pointer"
                                                        _hover={{ textDecoration: 'underline' }}
                                                        onClick={() => handleNavigate(contact?._id)}
                                                        fontSize="sm"
                                                        whiteSpace="normal"
                                                    >
                                                        {contact.phone}
                                                    </Text>
                                                ) : (
                                                    <Text fontSize="sm" color="gray.400" fontStyle="italic" whiteSpace="normal">No phone</Text>
                                                )}
                                                {contact.email ? (
                                                    <Tooltip label={contact.email} placement="top">
                                                        <Text
                                                            fontSize="xs"
                                                            color="var(--brand-primary)"
                                                            cursor="pointer"
                                                            _hover={{ textDecoration: 'underline' }}
                                                            onClick={() => handleNavigate(contact?._id)}
                                                            isTruncated
                                                            maxW="160px"
                                                        >
                                                            {contact.email}
                                                        </Text>
                                                    </Tooltip>
                                                ) : (
                                                    <Text fontSize="xs" color="gray.300" fontStyle="italic">No email</Text>
                                                )}
                                            </VStack>
                                        </Td>
                                        <Td py={4} borderColor="gray.200">
                                            {contact.whatsapp_available ? (
                                                <VStack align="start" spacing={1}>
                                                    <Tag
                                                        color={"#008236"}
                                                        bg={"#f0fdf4"}
                                                        border={"1px solid #b9f8cf"}
                                                        fontSize="xs"
                                                        size={"sm"}
                                                    >
                                                        <Flex align="center" gap={1}>
                                                            <IoChatbubbleOutline size={12} />
                                                            Available
                                                        </Flex>
                                                    </Tag>
                                                    {contact.whatsapp_first_delivered && (
                                                        <Text fontSize="xs" color="gray.400" whiteSpace="normal">
                                                            First delivered:{' '}
                                                            {new Date(contact.whatsapp_first_delivered).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric',
                                                            })}
                                                        </Text>
                                                    )}
                                                </VStack>
                                            ) : (
                                                <Tag
                                                    bg={"#fbf9fa"}
                                                    fontSize="xs"
                                                    fontWeight="medium"
                                                    border={"1px solid #ebe6e7"}
                                                    color={"#6a7282"}
                                                    size={"sm"}
                                                >
                                                    <Flex align="center" gap={1}>
                                                        <IoChatbubbleOutline size={12} />
                                                        Not Available
                                                    </Flex>
                                                </Tag>
                                            )}
                                        </Td>
                                        <Td py={4} borderColor="gray.200">
                                            {contact?.subscriber?.subscriber_id && contact?.subscriber?.created_at ? (
                                                <VStack align="start" spacing={1}>
                                                    <Flex align="center" gap={2}>
                                                        {getPlatformIcon(contact.subscriber) === 'android' ? (
                                                            <FaAndroid size={16} color="#00a63e" />
                                                        ) : getPlatformIcon(contact.subscriber) === 'ios' ? (
                                                            <FaApple size={16} color="#000000" />
                                                        ) : (
                                                            <FiSmartphone size={16} color="gray" />
                                                        )}
                                                        <Tooltip label={contact.subscriber.subscriber_id} placement="top">
                                                            <Text fontWeight="medium" fontSize="sm" color="gray.900">
                                                                {truncateSubscriberId(contact.subscriber.subscriber_id)}
                                                            </Text>
                                                        </Tooltip>
                                                    </Flex>
                                                    <Text fontSize="xs" color="gray.400" whiteSpace="normal">
                                                        Registered: {new Date(contact.subscriber.created_at).toLocaleDateString('en-GB', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })}
                                                    </Text>
                                                </VStack>
                                            ) : (
                                                <Flex alignItems="center" gap={2}>
                                                    <Text fontSize="sm" color="gray.400" fontStyle="italic" whiteSpace="normal">
                                                        No Subscriber
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Td>
                                        <Td py={4} borderColor="gray.200" maxW="260px">
                                            <VStack align="start" spacing={2}>
                                                {Array.isArray(contact?.tags) && contact.tags.length > 0 && (() => {
                                                    const MAX_VISIBLE = 3;
                                                    const visible = contact.tags.slice(0, MAX_VISIBLE);
                                                    const hidden = contact.tags.slice(MAX_VISIBLE);
                                                    return (
                                                        <Flex align="center" gap={2} flexWrap="wrap">
                                                            <LuTag size={14} color="gray" />
                                                            <HStack spacing={1} flexWrap="wrap">
                                                                {visible.map((tag, index) => {
                                                                    const tagName = tag.name || tag.label || tag._id;
                                                                    const colors = getConsistentColor(tagName);
                                                                    return (
                                                                        <Tag
                                                                            key={tag._id || index}
                                                                            size={"sm"}
                                                                            fontSize="xs"
                                                                            fontWeight="medium"
                                                                            bg={colors.bg}
                                                                            color={colors.text}
                                                                            border="1px solid"
                                                                            borderColor={colors.border}
                                                                        >
                                                                            {tagName}
                                                                        </Tag>
                                                                    );
                                                                })}
                                                                {hidden.length > 0 && (
                                                                    <Tooltip label={hidden.map((t) => t.name || t.label || t._id).join(', ')}>
                                                                        <Tag size="sm" fontSize="xs" fontWeight="medium" bg="gray.100" color="gray.600" border="1px solid" borderColor="gray.200" cursor="default">
                                                                            +{hidden.length} more
                                                                        </Tag>
                                                                    </Tooltip>
                                                                )}
                                                            </HStack>
                                                        </Flex>
                                                    );
                                                })()}
                                                {Array.isArray(contact?.active_cohorts) && contact.active_cohorts.length > 0 && (() => {
                                                    const MAX_VISIBLE = 2;
                                                    const visible = contact.active_cohorts.slice(0, MAX_VISIBLE);
                                                    const hidden = contact.active_cohorts.slice(MAX_VISIBLE);
                                                    return (
                                                        <Flex align="center" gap={2} flexWrap="wrap">
                                                            <LuUsers size={14} color="gray" />
                                                            <HStack spacing={1} flexWrap="wrap">
                                                                {visible.map((cohort, index) => {
                                                                    const colors = getConsistentColor(cohort.cohort_name);
                                                                    return (
                                                                        <Tag
                                                                            key={cohort._id || index}
                                                                            as="span"
                                                                            size={"sm"}
                                                                            fontSize="xs"
                                                                            fontWeight="medium"
                                                                            bg={colors.bg}
                                                                            color={colors.text}
                                                                            border="1px solid"
                                                                            borderColor={colors.border}
                                                                        >
                                                                            {cohort.cohort_name}
                                                                        </Tag>
                                                                    );
                                                                })}
                                                                {hidden.length > 0 && (
                                                                    <Tooltip label={hidden.map((c) => c.cohort_name).join(', ')}>
                                                                        <Tag size="sm" fontSize="xs" fontWeight="medium" bg="gray.100" color="gray.600" border="1px solid" borderColor="gray.200" cursor="default">
                                                                            +{hidden.length} more
                                                                        </Tag>
                                                                    </Tooltip>
                                                                )}
                                                            </HStack>
                                                        </Flex>
                                                    );
                                                })()}
                                                {(!contact.tags || contact.tags.length === 0) &&
                                                    (!contact.active_cohorts || contact.active_cohorts.length === 0) && (
                                                        <Text fontSize="sm" color="gray.400">
                                                            No tags or cohorts
                                                        </Text>
                                                    )}
                                            </VStack>
                                        </Td>
                                        <Td py={4} borderColor="gray.200">
                                            <VStack align="start" spacing={0}>
                                                {contact.country_id && countryMap[contact.country_id] ? (
                                                    <Text fontSize="sm" color="gray.600" whiteSpace="normal">
                                                        {countryMap[contact.country_id]}
                                                    </Text>
                                                ) : (
                                                    <Text fontSize="sm" color="gray.400" fontStyle="italic">—</Text>
                                                )}
                                                {contact.timezone && (
                                                    <Text fontSize="xs" color="gray.400" whiteSpace="normal">
                                                        {contact.timezone}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </Td>
                                        <Td py={4} borderColor="gray.200">
                                            <Text fontSize="sm" color="gray.600" whiteSpace="normal">
                                                {contact.updated_at
                                                    ? new Date(contact.updated_at).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric',
                                                    })
                                                    : <Text as="span" color="gray.400" fontStyle="italic">Never updated</Text>}
                                            </Text>
                                        </Td>
                                        <Td py={4} borderColor="gray.200">
                                            <Menu>
                                                <MenuButton
                                                    as={IconButton}
                                                    icon={<FiMoreVertical />}
                                                    variant="ghost"
                                                    size="sm"
                                                    aria-label="More actions"
                                                />
                                                <MenuList>
                                                    {editPermission && (
                                                        <MenuItem
                                                            icon={<FiEdit size={16} />}
                                                            onClick={() => onEdit(contact)}
                                                        >
                                                            Edit Contact
                                                        </MenuItem>
                                                    )}
                                                    {deletePermission && (
                                                        <>
                                                            <MenuDivider />
                                                            <MenuItem
                                                                icon={<FiTrash2 size={16} />}
                                                                color="red.600"
                                                                onClick={() => handleDeleteClick(contact)}
                                                            >
                                                                Delete Contact
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                </MenuList>
                                            </Menu>
                                        </Td>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>

                {pagination.total > pagination.limit && (
                    <Box p={6} borderTop="1px" borderColor="gray.200">
                        <Flex justify="center">
                            <Box className="pagination-wrapper">
                                <Pagination
                                    activePage={pagination.currentPage}
                                    itemsCountPerPage={pagination.limit}
                                    totalItemsCount={pagination.total}
                                    pageRangeDisplayed={5}
                                    onChange={onPageChange}
                                    itemClass="page-item"
                                    linkClass="page-link"
                                    activeClass="active"
                                    activeLinkClass="active-link"
                                />
                            </Box>
                        </Flex>
                    </Box>
                )}
            </Box>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                isOpen={isOpen}
                onClose={onClose}
                contact={selectedContactForDelete}
                onDelete={handleConfirmDelete}
                isDeleting={isDeleting}
            />
        </>
    );
};